'use client';

/* ──────────────────────────────────────────────
 *  En-tête de progression du tunnel. Les étapes
 *  comptées viennent de la machine (`visibleSteps`),
 *  jamais d'une logique de saut propre au composant.
 *  - large (≥ 900 px) : étapes numérotées et nommées,
 *    étape faite cochée ;
 *  - colonne unique : « ‹ » (étape précédente), nom de
 *    l'étape et compteur, barre fine dessous.
 *  « Retour au site » (hôte site vitrine) dans les deux.
 * ────────────────────────────────────────────── */

import { useMemo, type JSX } from 'react';
import { useBooking } from '../context/BookingContext';
import { useStepNav } from '../context/StepNavContext';
import { visibleSteps, type VisibleBookingStep } from '../context/booking-machine';
import type { BookingLabels } from '../types';

const stepName = (step: VisibleBookingStep, labels: BookingLabels): string =>
  ({
    services: labels.step_service,
    place: labels.step_place,
    zone_address: labels.step_zone_address,
    slot: labels.step_slot,
    checkout: labels.step_checkout,
  })[step];

const fillCounter = (template: string, step: string, index: number, total: number): string =>
  template.replace('{step}', step).replace('{index}', String(index)).replace('{total}', String(total));

export function BookingProgress(): JSX.Element | null {
  const { currentStep, ctx, labels, backToSiteUrl, releasePreReservation } = useBooking();
  const stepNav = useStepNav();
  const steps = useMemo((): VisibleBookingStep[] => visibleSteps(ctx), [ctx]);

  if (currentStep === 'loading' || currentStep === 'error' || currentStep === 'success') return null;

  const siteLink = backToSiteUrl ? (
    <a className="bt-progress__site-link" href={backToSiteUrl} onClick={releasePreReservation}>
      {labels.back_to_site}
    </a>
  ) : null;

  /* Demande de devis : hors parcours, un en-tête « ‹ Demande de devis » sans compteur. */
  if (currentStep === 'quote') {
    return (
      <nav className="bt-progress bt-progress--steps" aria-label={labels.progress_label}>
        <div className="bt-progress__compact bt-progress__compact--always">
          {stepNav?.onBack ? (
            <button type="button" className="bt-progress__back" onClick={stepNav.onBack} aria-label={labels.back}>
              <span aria-hidden="true">‹</span>
            </button>
          ) : (
            <span className="bt-progress__back-spacer" />
          )}
          <span className="bt-progress__counter">{labels.step_quote}</span>
          {siteLink ?? <span className="bt-progress__back-spacer" />}
        </div>
      </nav>
    );
  }

  const currentIndex = steps.indexOf(currentStep);
  if (currentIndex === -1) return null;
  const position = currentIndex + 1;
  const total = steps.length;
  const currentName = stepName(currentStep, labels);

  return (
    <nav className="bt-progress bt-progress--steps" aria-label={labels.progress_label}>
      <div className="bt-progress__wide">
        {siteLink ? <span className="bt-progress__wide-site">← {siteLink}</span> : <span />}
        <ol className="bt-progress__steps">
          {steps.map((step, index): JSX.Element => {
            const state = index < currentIndex ? 'done' : index === currentIndex ? 'current' : 'todo';
            return (
              <li
                key={step}
                className={`bt-progress__step bt-progress__step--${state}`}
                aria-current={state === 'current' ? 'step' : undefined}
              >
                <span className="bt-progress__dot" aria-hidden="true">
                  {state === 'done' ? '✓' : index + 1}
                </span>
                <span className="bt-progress__name">{stepName(step, labels)}</span>
              </li>
            );
          })}
        </ol>
      </div>

      <div className="bt-progress__compact">
        {stepNav?.onBack ? (
          <button type="button" className="bt-progress__back" onClick={stepNav.onBack} aria-label={labels.back}>
            <span aria-hidden="true">‹</span>
          </button>
        ) : (
          <span className="bt-progress__back-spacer" />
        )}
        <span className="bt-progress__counter">
          <span aria-hidden="true">{fillCounter(labels.progress_counter, currentName, position, total)}</span>
          <span className="bt-sr-only">{fillCounter(labels.progress_counter_aria, currentName, position, total)}</span>
        </span>
        {siteLink ?? <span className="bt-progress__back-spacer" />}
      </div>

      <div className="bt-progress__bar" aria-hidden="true">
        <div className="bt-progress__fill" style={{ width: `${(position / total) * 100}%` }} />
      </div>
    </nav>
  );
}
